import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

/**
 * Searchable dropdown for master-data fields (managers, designations, etc.)
 */
const SearchableSelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Search…',
  triggerPlaceholder = null,
  required = false,
  disabled = false,
  error = null,
  helpText = null,
  onSearch = null,
  loading = false,
  isClearable = false,
  className = '',
  buttonClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => 
      (o.label || '').toLowerCase().includes(q) || 
      (o.subLabel || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && onSearch) onSearch(query);
  }, [query, open, onSearch]);

  const triggerText = selected
    ? selected.label
    : (triggerPlaceholder || (placeholder && !placeholder.toLowerCase().startsWith('search') ? placeholder : `Select ${label || 'option'}`));

  const searchInputPlaceholder = placeholder && placeholder.toLowerCase().startsWith('select')
    ? placeholder.replace(/^select/i, 'Search')
    : (placeholder || 'Search…');

  return (
    <div className={`space-y-1 relative ${open ? 'z-[100]' : 'z-10'} ${className}`} ref={wrapRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen(!open)}
          className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-left text-sm transition ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
          } ${disabled ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'text-gray-900'} ${buttonClassName}`}
        >
          <span className={`truncate mr-2 ${selected ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
            {triggerText}
          </span>
          <div className="flex items-center space-x-1 shrink-0">
            {isClearable && selected && !disabled && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                }}
                className="p-0.5 text-gray-400 hover:text-gray-600 rounded transition"
                title="Clear selection"
              >
                <X className="w-3.5 h-3.5" />
              </span>
            )}
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {open && (
          <div className="absolute z-[100] mt-1 w-full bg-white border border-gray-200 rounded-md shadow-xl max-h-60 overflow-hidden">
            <div className="p-2 border-b flex items-center gap-2">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchInputPlaceholder}
                className="flex-1 text-sm outline-none"
                autoFocus
              />
              {query && (
                <button type="button" onClick={() => setQuery('')}><X className="w-3 h-3 text-gray-400" /></button>
              )}
            </div>
            <ul className="max-h-48 overflow-y-auto">
              {loading && <li className="px-3 py-2 text-sm text-gray-500">Loading…</li>}
              {!loading && filtered.length === 0 && (
                <li className="px-3 py-2 text-sm text-gray-500">No matches</li>
              )}
              {filtered.map((o) => (
                <li key={o.value}>
                  <button
                    type="button"
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                      String(o.value) === String(value) ? 'bg-blue-100 font-medium' : ''
                    }`}
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    {o.label}
                    {o.subLabel && <span className="block text-xs text-gray-500">{o.subLabel}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {helpText && !error && <p className="text-xs text-gray-500">{helpText}</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}
    </div>
  );
};

export default SearchableSelect;
